AoPS.Utils.initKeyChain(AoPS, "BackboneExtras");

AoPS.BackboneExtras = (function (BackboneExtras) {
	// Dependencies
	var Utils = AoPS.Utils;

	/**
		Defines an equals Handlebars block helper to obtain a slightly better
		conditional than a simple if. This tests for equality between two
		expressions.

		An example, where difficulty is a key in the provided context:
			{{#equals difficulty "easy"}}
				Wuss.
			{{else}}
				Oh you think you're so tough.
			{{/equals}}
	*/
	Handlebars.registerHelper("equals", function (a, b, options) {
		// eslint-disable-next-line eqeqeq
		if (a == b) {
			return options.fn(this);
		} else {
			return options.inverse(this);
		}
	});

	/**
   		Defines a notEquals Handlebars block helper to test for inequality between two
   		expressions.
 	*/
	Handlebars.registerHelper("notEquals", function (a, b, options) {
		// eslint-disable-next-line eqeqeq
		if (a != b) {
			return options.fn(this);
		} else {
			return options.inverse(this);
		}
	});

	/**
		Defines an ifCond Handlebars block helper to support common conditional statements.
	*/
	Handlebars.registerHelper("ifCond", function (v1, operator, v2, options) {
		switch (operator) {
			case "===":
				return v1 === v2 ? options.fn(this) : options.inverse(this);
			case "!==":
				return v1 !== v2 ? options.fn(this) : options.inverse(this);
			case "<":
				return v1 < v2 ? options.fn(this) : options.inverse(this);
			case "<=":
				return v1 <= v2 ? options.fn(this) : options.inverse(this);
			case ">":
				return v1 > v2 ? options.fn(this) : options.inverse(this);
			case ">=":
				return v1 >= v2 ? options.fn(this) : options.inverse(this);
			case "&&":
				return v1 && v2 ? options.fn(this) : options.inverse(this);
			case "||":
				return v1 || v2 ? options.fn(this) : options.inverse(this);
			default:
				return options.inverse(this);
		}
	});

	/**
	 * Lets a user define a variable from inside the handlebars file.
	 * Usage:
	 * {{assign 'bestDogEver' 'Reggie'}}
	 * {{@root.bestDogEver}} --> will evalute to Reggie
	 */
	Handlebars.registerHelper("assign", function (varName, varValue, options) {
		if (!options.data.root) {
			options.data.root = {};
		}
		options.data.root[varName] = varValue;
	});

	/**
		mixin BackboneExtras.AutocreateMixin

		A mixin intended for Backbone.Collection that adds a getOrCreate method.
		Existing collection methods are unchanged.

		Usage:
		AutocreateCollection = Backbone.Collection.extend(BackboneExtras.AutocreateMixin);
		my_collection = new AutocreateCollection();

		Methods:
		setCreateFunction(createFunction)
			Set the creation function, which accepts one parameter that can
			be anything passed to Backbone.Collection.get (id, cid, model).
		getOrCreate(id)
			Functions identically to Backbone.Collection.get, but uses the
			previously set creation function to create and return a new model
			if the ID is not found in the collection.
	*/
	BackboneExtras.AutocreateMixin = {
		/**
			method setCreateFunction

			Sets the creation function used by the collection when a getOrCreate
			call is made and it does not find an associated item. Recommended
			to call on or right after construction of the collection.

			@param createFunction: A function taking one argument, which can
				be anything that might be passed to getOrCreate. It should
				return a model, which will automatically be added to the
				collection. If the truth value of the return is false, it
				will not be added to the collection.
			@return None.
		*/
		setCreateFunction: function (createFunction) {
			this.createFunction = createFunction;
		},

		/**
			method getOrCreate

			A drop-in replacement for the usual Backbone.Collection.get method,
			which will attempt to add a new model to the collection if it is
			not found. In this way we have a method that guarantees returning
			a model.

			You must call the setCreateFunction to specify how models are to
			be made before using this function.

			@param id: Can be anything that Backbone.Collection.get accepts,
				including model IDs, CIDs, or model objects.
			@param options: Object with various options. You can use any options
				for the Collection's add method in case the model is created.
				These additional options are also available:
					delay_add: If the model must be created, it will not be added.
						The calling code will take responsibility for adding it.
				Currently, none of these options have an effect if the model existed.
			@return The existing model, or the newly made model returned from
				the create function, or undefined if the create function was
				needed but not specified beforehand.
		*/
		getOrCreate: function (id, options) {
			options = options || {};
			var add_options = _.omit(options, ["delay_add"]);

			var result = this.get(id);
			if (!result && !_.isFunction(this.createFunction)) {
				return void 0; // Needed createFunction but not provided yet
			} else if (!result) {
				result = this.createFunction(id);
				if (result && !options.delay_add) {
					this.add(result, add_options);
				}
			}
			return result;
		},
	};

	/**
		mixin BackboneExtras.TemplateMixin

		A mixin for Backbone.View. Provides methods for setting and rendering
		a Handlebars template, with the option of replacing the root element
		of the Backbone.View with the one in the template.

		Usage:
		TemplateView = Backbone.View.extend(BackboneExtras.TemplateMixin);
		my_view = new TemplateView();

		Methods:
		setTemplate(template_id, use_root_el)
			Set the Handlebars template and specify whether to use the root
			element.
		setTemplateAndUseRootEl(template_id)
			More readable alias for setTemplate(template_id, true).
		renderTemplate(context)
			Render the Handlebars template.
	*/
	BackboneExtras.TemplateMixin = {
		/**
			method setTemplate

			Sets the template ID to use in the render template method. Also
			allows specifying whether to replace the Backbone view element with
			the template's root element.

			You probably want to call this on the view initialization. However,
			it is safe to call this method again at a later point. You will
			only see changes after the next call to renderTemplate.

			@param template_id: The ID of the template. Only HTML ID attribute
				lookups are supported. You can either include or omit a #
				at the beginning of this parameter.
			@param use_root_el: Boolean, defaults to false. Specifies whether
				to replace the Backbone View element with the root element
				of the template. Do not set this to true if your template has
				more than one root element, as it will cause strange behavior.
			@return None.
		*/
		setTemplate: function (template_id, use_root_el) {
			if (template_id[0] !== "#") {
				template_id = "#" + template_id;
			}

			this.compiledTemplate = AoPS.View.compileTemplate(template_id);
			if (!this.compiledTemplate) {
				console.log("WARNING: Template ID " + template_id + " not found");
			}

			this.use_root_el = !!use_root_el;
			this.rendered_tpl_before = false;
		},

		/**
			method setTemplateAndUseRootEl

			A readable alias of setTemplate with the use_root_el argument
			set to true.

			@param template_id: See the template_id parameter description
				under setTemplate.
		*/
		setTemplateAndUseRootEl: function (template_id) {
			this.setTemplate(template_id, true);
		},

		/**
			method renderTopOnly

			A bit of a hack that renders the top-level tag of a template only, so
			that a future call to renderTemplate won't have to replace this.$el
			and render any references to it invalid. Written to workaround a
			Page.js behavior.

			If the root element is not being used, this is a no-op.
		*/
		renderTopOnly: function () {
			var rendered_tpl;
			var rendered_top;

			if (this.compiledTemplate) {
				rendered_tpl = $.trim(this.compiledTemplate({}));

				if (rendered_tpl === "") {
					// No-op
				} else if (!this.use_root_el) {
					// No-op; not using root element
					this.rendered_tpl_before = true;
				} else if (this.rendered_tpl_before) {
					// No-op; top-level tag already switched out
				} else {
					rendered_top = $($.parseHTML(rendered_tpl)).empty();
					this.$el.replaceWith(rendered_top);
					this.setElement(rendered_top);
					this.rendered_tpl_before = true;
				}
			} else {
				this.$el.empty();
			}
		},

		/**
			method renderTemplate

			Empties the view of all contents, then renders the template inside
			of it. Accounts for the settings about whether to do root element
			replacement and handles all the associated logic.

			If you are using subviews, make sure you detach all of them before
			calling this method.

			@param context: The context to pass to the template for rendering.
				This is the usual parameter to pass to a Handlebars compiled
				template.
		*/
		renderTemplate: function (context) {
			var rendered_tpl;
			var rendered_el;

			if (this.compiledTemplate) {
				rendered_tpl = $.trim(this.compiledTemplate(context));

				if (rendered_tpl === "") {
					// No-op
				} else if (!this.use_root_el) {
					// Don't worry about top-level tag replacing at all.
					this.$el.html(rendered_tpl);
					this.rendered_tpl_before = true;
				} else if (this.rendered_tpl_before) {
					// Since we've rendered before, leave the top-level tag untouched.
					// This preserves showing/hiding of this.$el among other things.
					this.$el.empty();
					this.$el.append($($.parseHTML(rendered_tpl)).contents());
				} else {
					// Replace top-level view tag with the template's.
					rendered_el = this.$el.html(rendered_tpl).contents();
					this.$el.replaceWith(rendered_el);
					this.setElement(rendered_el);
					this.rendered_tpl_before = true;
				}
			} else {
				this.$el.empty();
			}
		},
	};

	/**
		Defines a SUBVIEWLOC Handlebars helper intended for use with the
		SubviewMixin. See the documentation of its location system for how
		to use it with this helper.

		To put this in your templates, use
			{{SUBVIEWLOC "a_location_name"}}
		or alternately
			{{subviewloc "a_location_name"}}
		but don't mix the case.

		Do not use this inside tables. HTML tables hate you and so the dummy tag
		this function makes will get thrown out of the table, causing a disaster.
		Just mark the location with a class and use that instead.
	*/
	function subviewLocHelper(loc, options) {
		var html;
		loc = Handlebars.Utils.escapeExpression(loc);
		html = '<a data-subviewloc="' + loc + '"></a>';
		return new Handlebars.SafeString(html);
	}
	Handlebars.registerHelper("SUBVIEWLOC", subviewLocHelper);
	Handlebars.registerHelper("subviewloc", subviewLocHelper);

	/**
		mixin BackboneExtras.SubviewMixin

		A mixin for Backbone.View. This gives a view facilities for managing
		other views that live inside of it. The methods are opt-in, so a
		class using this mixin has no obligation to have subviews.

		The whole class uses a string-based location system to specify where
		subviews get added in the view's HTML. See the addSubviewToLocation
		documentation for details about it.

		If using this together with the TemplateMixin, a typical usage of this
		class is to call addSubviewToLocation as needed on initialization,
		and then have a render method that calls these methods in order:
			detachAllSubviews()
			renderTemplate(context)
			attachAllSubviews()
			removeHandlebarsLocationMarkers()
		with any custom processing done before or after these calls. You should
		also call closeAllSubviews() in your close method. The other methods
		in this mixin are for when more advanced usage is required.

		Usage:
		ContainerView = Backbone.View.extend(BackboneExtras.SubviewMixin);
		my_view = new ContainerView();

		Methods:
		applyToAllAtLocation(location, applyFunc)
			Higher-order function; executes applyFunc on all subviews in a
			given location.
		applyToAllSubviews(applyFunc)
			Higher-order function; executes applyFunc on all subviews.
		isLocationEmpty(location)
			Returns whether the given location is empty.
		getLocationList()
			Returns an array with all locations with subviews.
		clearLocation(location)
			Removes all subviews from the given location.
		addSubviewToLocation(view_obj, location)
			Adds a subview to a given location.
		detachAllSubviews()
			Calls the jQuery detach method on all subviews.
		renderAllSubviews()
			Calls the render method on all subviews.
		attachAllSubviews()
			Attaches all subviews to this view's HTML by processing the
			provided locations.
		removeHandlebarsLocationMarkers()
			Removes all dummy HTML elements added by the Handlebars SUBVIEWLOC
			helper.
		closeAllSubviews()
			Calls the close method on all subviews, then removes all subviews
			from this view.
	*/
	BackboneExtras.SubviewMixin = {
		/**
			method reorderAtLocation

			A higher-order function for reordering subviews added to a location.

			There is no return; this function does the reordering in-place internally.
			Note that there will be no effect on the view's HTML until the next call
			to render.

			@param location: The location of the subviews to reorder.
			@param cmpFunc: A function taking the subview object and returning a
				value which can be compared to other returns with comparison operators.
				The subviews are sorted in increasing order (1st < 2nd < ... < last).
				The function will be automatically bound to the containing view as
				the context.
			@param wait: Optional. Set to true to not automatically reorder the
				subviews within the DOM and wait for the next render.
			@return None.
		*/
		reorderAtLocation: function (location, cmpFunc, wait) {
			Utils.initKeyChain(this, ["subviews", location], []);
			var len = this.subviews[location].length;
			if (!len) {
				return;
			}

			var orig_last = this.subviews[location][len - 1];

			cmpFunc = _.bind(cmpFunc, this);
			var list = _.map(
				this.subviews[location],
				function (subview) {
					return {
						cmp: cmpFunc(subview),
						view: subview,
					};
				},
				this
			);
			list.sort(function (a, b) {
				return a.cmp < b.cmp ? -1 : 1;
			});
			this.subviews[location] = _.pluck(list, "view");

			// Reordering done; stop here if we shouldn't affect the DOM.
			if (wait) {
				return;
			}

			// Assuming here that all elements in the location are consecutive in the DOM.

			// Get the last element into the correct spot.
			var new_last = this.subviews[location][len - 1];
			if (new_last !== orig_last) {
				orig_last.$el.after(new_last.$el);
			}
			// Then go in order inserting each before the last, which gets it all right.
			_.each(this.subviews[location].slice(0, -1), function (v) {
				new_last.$el.before(v.$el);
			});
		},

		/**
			method applyToAllAtLocation

			A higher-order function for running a function on all subviews in a
			given location.

			@param location: The location of the subviews to iterate over.
			@param applyFunc: A function taking two arguments, the subview
				object and the index. The function will be automatically bound to the
				containing view as the context.
			@return None.
		*/
		applyToAllAtLocation: function (location, applyFunc) {
			Utils.initKeyChain(this, ["subviews", location], []);
			_.each(this.subviews[location], _.bind(applyFunc, this));
		},

		/**
			method applyToAllSubviews

			A higher-order function for running a function on all subviews.

			@param applyFunc: A function taking three arguments, the location
				of the subview, the subview object, and the index. The function will be
				automatically bound to the containing view as the context.
			@return None.
		*/
		applyToAllSubviews: function (applyFunc) {
			Utils.initKeyChain(this, "subviews");
			_.each(
				this.subviews,
				function (view_list, location) {
					this.applyToAllAtLocation(location, _.partial(applyFunc, location));
				},
				this
			);
		},

		/**
			method isLocationEmpty

			Checks whether there are any subviews in the given location.

			@param location: The location to check.
			@return Boolean. True if the location has no subviews, else false.
		*/
		isLocationEmpty: function (location) {
			Utils.initKeyChain(this, ["subviews", location], []);
			return !this.subviews[location].length;
		},

		/**
			method getLocationList

			Returns an array of all locations that have subviews in them.

			@return Array of strings, giving all locations with subviews.
		*/
		getLocationList: function () {
			Utils.initKeyChain(this, "subviews");
			return _.reject(
				_.keys(this.subviews),
				_.bind(this.isLocationEmpty, this)
			);
		},

		/**
			method clearLocation

			Removes all subviews in the given location from the internal
			register and calls the close method on those subviews.

			@param location: The location to clear.
			@param no_close: Optional; if true, subviews are merely removed from the
				location without being closed, and can be reused later.
			@return None.
		*/
		clearLocation: function (location, no_close) {
			Utils.initKeyChain(this, ["subviews", location], []);
			this.applyToAllAtLocation(location, function (view_obj) {
				if (!no_close) {
					this.closeView(view_obj);
				} else {
					view_obj.$el.detach();
				}
			});
			this.subviews[location] = [];
		},

		/**
			method addSubviewToLocation

			Adds a subview to the internal register at the given location.

			The location can be any valid jQuery selector. Unless a special
			value is given, a subview is added to the HTML simply with
				this.$el.find(location).append(subview);
			You should make sure the selector will only find one element;
			otherwise undesirable behavior may occur.

			There are two ways that a location can be processed differently. The
			first is to provide the empty string. Subviews in location ""
			are appended directly to the view, as in
				this.$el.append(subview);

			The second is to begin the location with '@'. This is how one uses
			the SUBVIEWLOC Handlebars helper. If your template contains
				{{SUBVIEWLOC "right_here"}}
			then you can add subviews there by specifying the location as
			"@right_here".

			@param view_obj: The Backbone.View object to add as a subview.
			@param location: String, defaulting to the empty string.
				The location to add the subview to.
			@return None.
		*/
		addSubviewToLocation: function (view_obj, location) {
			location = location || "";
			Utils.initKeyChain(this, ["subviews", location], []);
			this.subviews[location].push(view_obj);
		},

		/**
			method detachAllSubviews

			Calls the jQuery detach method on all subviews.

			@return None.
		*/
		detachAllSubviews: function () {
			this.applyToAllSubviews(function (location, view_obj) {
				view_obj.$el.detach();
			});
		},

		/**
			method detachAllSubviews

			Calls the render method on all subviews.

			@return None.
		*/
		renderAllSubviews: function () {
			this.applyToAllSubviews(function (location, view_obj) {
				view_obj.render();
			});
		},

		/**
			method attachToHandlebarsLocation

			Adds subviews to a location specified by the Handlebars SUBVIEWLOC
			helper. Helper function for attachAllSubviews; not intended for
			public use.

			@param view_list: Array of view objects to add.
			@param hb_location: The Handlebars location without the '@'.
			@return None.
		*/
		attachToHandlebarsLocation: function (view_list, hb_location) {
			var candidates = this.$('a[data-subviewloc="' + hb_location + '"]');
			if (candidates.length) {
				candidates.first().before(view_list);
			}
		},

		/**
			method attachAllSubviews

			Attaches each subview in the proper place in the view based on
			its provided location. This method will properly aggregate all
			subviews in a given location before appending to avoid unnecessary
			DOM reflows.

			@return None.
		*/
		attachAllSubviews: function () {
			var view_table = {};

			_.each(
				this.getLocationList(),
				function (location) {
					var view_list = [];
					this.applyToAllAtLocation(location, function (view_obj) {
						view_list.push(view_obj.$el);
					});
					view_table[location] = view_list;
				},
				this
			);

			_.each(
				this.getLocationList(),
				function (location) {
					var view_list = view_table[location];
					if (location === "") {
						this.$el.append(view_list);
					} else if (location[0] === "@") {
						this.attachToHandlebarsLocation(view_list, location.slice(1));
					} else {
						this.$(location).append(view_list);
					}
				},
				this
			);
		},

		/**
			method removeHandlebarsLocationMarkers

			The Handlebars SUBVIEWLOC helper inserts dummy HTML elements to make
			this mixin's magic '@' locations work. These are simple <a> tags
			with a custom data-subviewloc attribute. You probably don't want
			these bogus elements lying around, so call this method right after
			using attachAllSubviews to get rid of all of them.

			If you don't properly clean these up and you have views inside views
			with naming conflicts in SUBVIEWLOC-specified locations, you may
			get some very bizarre behavior where subviews are added in more
			than one place. So make sure to call this.

			@return None.
		*/
		removeHandlebarsLocationMarkers: function () {
			this.$("a[data-subviewloc]").remove();
		},

		/**
			method closeAllSubviews

			Calls the close method on all subviews.

			@return None.
		*/
		closeAllSubviews: function () {
			this.applyToAllSubviews(function (location, view_obj) {
				this.closeView(view_obj);
			});
			this.subviews = {};
		},

		/**
			method closeView

			Removes a view and unbinds events on it. Called internally to
			clean up subviews.

			@return None.
		*/
		closeView: function (view_obj) {
			view_obj.remove();
			view_obj.unbind();
			if (view_obj.onClose) {
				view_obj.onClose();
			}
		},
	};

	return BackboneExtras;
})(AoPS.BackboneExtras);
